import Subscription from "@repo/kikoff-models/Subscription";
import Telemetry from "@repo/telemetry";
import Country from "@repo/utils/Country";
import Interval from "@repo/utils/Interval";
import Locale from "@repo/utils/Locale";
import Money from "@repo/utils/Money";
import UDate from "@repo/utils/UDate";

import { AnalyticsEvents } from "./analytics/client";

type AppSettings = {
  country: Country;
  extraRegionCodes?: (Country | (string & {}))[];
  currency: Money.Currency;
  dateFormat: Parameters<typeof UDate.parse>[1];
  locales: [Locale, ...Locale[]];
  support: {
    email: string;
    phone: {
      idvFail?: string;
      emailVerification?: string;
    };
    satisfactionGuarantee: boolean;
  };
  auth: {
    googleSso: boolean;
    forgotEmail: boolean;
    mfa: boolean;
  };
  phone: {
    resendPhoneCode: boolean;
    requireMarketingSmsConsent: boolean;
  };
  marketing: {
    facebookId: string;
    gtmId: string;
    gtagId: string;
    tiktokId: string;
    podscribeId?: string;
    clarityId?: string;
    impactRadius: {
      trackingIdByConversionEvent: Record<
        keyof AnalyticsEvents["conversion"],
        number
      >;
    };
    ltvByTier: Record<Subscription.Tier, number>;
  };
  expectedScoreIncreaseBuckets: Interval.Map<number>;
  rentReporting: boolean;
  creditAccount: {
    activationRedirect?: string;
    closeRedirect?: string;
  };
  helloPrivacy: boolean;
};

const appSettings = (
  import.meta.env.API_ADAPTER === "us"
    ? ({
        country: "US",
        extraRegionCodes: ["PR", "AS", "GU", "VI"],
        currency: "USD",
        locales: ["en", "es"],
        dateFormat: "mm/dd/yyyy",
        auth: {
          googleSso: true,
          forgotEmail: true,
          mfa: true,
        },
        phone: {
          resendPhoneCode: true,
          requireMarketingSmsConsent: false,
        },
        support: {
          email: "support@kikoff.com",
          // See more phone numbers here: https://docs.google.com/spreadsheets/d/1UhJqX4K4zADZITKvHy0YrDZ9HECFYOIsIwYCD9ZQslo/edit?gid=0#gid=0
          phone: {
            idvFail: "+1 (844) 741-9290",
            emailVerification: "+1 (844) 741-9269",
          },

          satisfactionGuarantee: true,
        },
        marketing: {
          facebookId: "759796597839521",
          clarityId: "g95f9ieic7",
          gtmId: "GTM-KZDXSSZ",
          gtagId: "G-6203WK809M",
          tiktokId: "C11A1S104P0HGS266FGG",
          podscribeId: "d8656980-7862-4277-ac8d-6a5f81a2dc01",
          impactRadius: {
            trackingIdByConversionEvent: {
              Signup: 28111,
              "Credit Account Open": 28335,
              // TODO
              "Make Credit Line Payment": 0,
            },
          },
          ltvByTier: {
            basic: 44,
            premium: 200,
            ultimate: 300,
          },
        },
        expectedScoreIncreaseBuckets: {
          "[300,400)": 159,
          "[400,500)": 101,
          "[500,600)": 47,
        },
        rentReporting: true,
        creditAccount: {
          activationRedirect: "/dashboard/store/onboarding-survey",
          closeRedirect: "/dashboard/account/united-account-closure/ca",
        },
        helloPrivacy: true,
      } satisfies AppSettings)
    : ({
        country: "CA",
        currency: "CAD",
        locales: ["en", "fr"],
        dateFormat: "yyyy-mm-dd",
        auth: {
          googleSso: false,
          forgotEmail: false,
          mfa: false,
        },
        phone: {
          resendPhoneCode: false,
          requireMarketingSmsConsent: true,
        },
        support: {
          email: "support@kikoff.ca",
          satisfactionGuarantee: false,
          phone: {},
        },
        marketing: {
          facebookId: "3934492436832739",
          gtmId: "GTM-KZDXSSZ",
          // For google adwords
          gtagId: "AW-590059496",
          tiktokId: "C11A1S104P0HGS266FGG",
          impactRadius: {
            trackingIdByConversionEvent: {
              // TODO
              Signup: 0,
              "Credit Account Open": 49795,
              "Make Credit Line Payment": 50609,
            },
          },
          ltvByTier: {
            basic: 55,
            premium: 185,
            get ultimate() {
              Telemetry.captureError(
                new Error(
                  "Attempted to get LTV for unsupported ultimate plan in Canada."
                )
              );

              return 0;
            },
          },
        },
        expectedScoreIncreaseBuckets: {},
        rentReporting: false,
        creditAccount: {},
        helloPrivacy: false,
      } satisfies AppSettings)
) as AppSettings;

Country.current = appSettings.country;
Money.currency = appSettings.currency;

export default appSettings;

type ServerAppSettings = {
  marketing: {
    tiktokAccessToken: string;
  };
};

// App settings that should only be accessed in server functions
export const serverAppSettings =
  import.meta.env.API_ADAPTER === "us"
    ? ({
        marketing: {
          tiktokAccessToken: "b72d08e6b8b13ad7ae950acb7547a20614b312c9",
        },
      } satisfies ServerAppSettings)
    : ({
        marketing: {
          // TODO: Check if Canada has a different access token
          tiktokAccessToken: "b72d08e6b8b13ad7ae950acb7547a20614b312c9",
        },
      } satisfies ServerAppSettings);
